/**
 * Stays API endpoints.
 *
 * A stay is FHIR Encounter in SHAPE, not in vocabulary: a ward says "stay", so
 * that is the noun on the wire (adr/0019). `encounter` is not an API noun.
 *
 * A stay has ONE parent, the patient, so every route here is a sub-resource
 * of `/patients/{patient_id}`. There is no `/locations/{id}/stays` and no
 * top-level `/stays`; a census is a cross-parent list with the `-` wildcard.
 *
 * The lifecycle transitions are AIP-136 custom methods
 * (`POST .../stays/{stay_id}:transfer`), never a verb path segment, and they
 * are addressed BY STAY. The retired `/patients/{id}/transfer` and
 * `/patients/{id}/discharge` resolved the open stay server-side; naming the
 * stay is what makes the write auditable when a patient has more than one.
 *
 * Registration (`POST /patients`) already opens the first stay, so these cover
 * everything after that.
 */

import { api } from '../client';
import type { Page } from '../types';
import { createLogger } from '@/utils/logger';
import { randomUUID } from '@/lib/uuid';

const logger = createLogger('StaysAPI');

/** Administrative stay status. Clinical presence is a separate field. */
export type StayStatus = 'planned' | 'admitted' | 'discharged' | 'cancelled';

/** Where the patient is, clinically: FHIR `Encounter.subjectStatus`. */
export type StayPresence = 'present' | 'on_leave';

/** One stay. Mirrors Go StayResponse. */
export interface StayResponse {
  id: string;
  patient: string;
  /** The stay's current unit; null once discharged or after a cancelled admit. */
  location: string | null;
  class: string;
  responsible_service?: string | null;
  status: StayStatus;
  presence: StayPresence;
  started_at: string;
  ended_at?: string | null;
  disposition?: string | null;
  destination?: string | null;
}

/** One row of the append-only movement log (FHIR EncounterHistory). */
export interface StayPlacementResponse {
  id: string;
  stay: string;
  seq: number;
  kind: string;
  location: string | null;
  recorded_at: string;
  actor: string;
}

export type StayListResponse = Page<StayResponse>;
export type StayPlacementListResponse = Page<StayPlacementResponse>;

const idem = (key?: string) => ({ headers: { 'Idempotency-Key': key ?? randomUUID() } });

/** A patient's stays, newest first. */
export async function listStays(patientId: string): Promise<StayResponse[]> {
  try {
    logger.debug('Listing stays', { patientId });
    const items = await api.getAllItems<StayListResponse>(
      `/patients/${encodeURIComponent(patientId)}/stays`,
    );
    logger.info('Stays listed', { patientId, count: items.length });
    return items;
  } catch (error) {
    logger.error('Failed to list stays', { patientId, error });
    throw error;
  }
}

function censusPath(filter?: string): string {
  const query = new URLSearchParams();
  if (filter) query.set('filter', filter);
  const qs = query.toString();
  return `/patients/-/stays${qs ? `?${qs}` : ''}`;
}

/**
 * Every open stay the caller may see, across every patient — the ward's
 * caseload as one read.
 *
 * The `-` wildcard is AIP-159's cross-parent read (api-service-go
 * HandleListStaysAcrossPatients): the alternative is a stay list per patient,
 * which is one request per bed on every render of a census screen. The filter
 * vocabulary (`stayFilterFields`) accepts `status`. Every page is read,
 * following the list's `Link: rel="next"`.
 */
export async function listAdmittedStays(): Promise<StayResponse[]> {
  try {
    logger.debug('Listing the census');
    return await api.getAllItems<StayListResponse>(censusPath('status = admitted'));
  } catch (error) {
    logger.error('Failed to list the census', { error });
    throw error;
  }
}

/**
 * The stay with this id, found through the census.
 *
 * A COLD DEEP LINK to `/stays/{id}` carries no patient, and a stay's
 * sub-resources are nested under one. This is how the board gets from the id in the
 * URL to the pair it needs; in-app navigation never comes here, because a stay
 * opened from a list already carries its `patient` field. The census has no
 * `id` filter (`stayFilterFields`), so a lookup by stay id is a walk over the
 * pages.
 *
 * It walks every page rather than reading the first one. A truncated search
 * would report a stay that exists as "not found" — the same thing a nurse sees
 * when she mistypes an id, with no way to tell the two apart. It stops at the
 * page that holds the match.
 *
 * Null is the answer for a stay this caller cannot see or that does not exist.
 * The two are deliberately the same answer here: the census is already scoped
 * to what the ACL allows, and distinguishing them would tell a caller that a
 * stay they may not read exists.
 */
export async function findStayAcrossPatients(
  stayId: string,
): Promise<StayResponse | null> {
  for (let next: string | null = censusPath(); next !== null; ) {
    const page: StayListResponse = await api.getPage<StayListResponse>(next);
    const match = page.items.find((s) => s.id === stayId);
    if (match) return match;
    next = page.next;
  }
  return null;
}

/** The append-only movement log for one stay. */
export async function listStayPlacements(
  patientId: string,
  stayId: string,
): Promise<StayPlacementResponse[]> {
  return api.getAllItems<StayPlacementListResponse>(
    `/patients/${encodeURIComponent(patientId)}/stays/${encodeURIComponent(stayId)}/placements`,
  );
}

/**
 * The patient's admitted stay, or null when there is none.
 *
 * Null is an answer, not a failure: a discharged patient HAS no admitted stay,
 * and the caller's job is to disable the transfer and discharge actions rather
 * than pick a stay on the patient's behalf. Guessing: the newest, the last
 * discharged: would move or close a stay the user did not name.
 *
 * Two admitted stays is not a case to choose between: the server opens a stay
 * only for a patient with none admitted, so two means the data is broken. The
 * caller is about to transfer or discharge ONE of them by id, and picking is
 * how a patient gets moved off a ward nobody meant to touch: with a record
 * that reads as a deliberate move. It throws instead.
 *
 * An admitted stay with no location is the same class of broken (the server
 * calls it ENCOUNTER_CORRUPT and fails closed on it). Returning it would light
 * up transfer and discharge on a chart the server will refuse, at the bedside,
 * with no way for the nurse to tell why.
 */
export function admittedStay(stays: StayResponse[]): StayResponse | null {
  const admitted = stays.filter((s) => s.status === 'admitted');
  if (admitted.length > 1) {
    throw new Error(
      `patient ${admitted[0].patient} has ${admitted.length} admitted stays (${admitted
        .map((s) => s.id)
        .join(', ')}); one of them is wrong and this cannot pick`,
    );
  }
  const stay = admitted[0];
  if (!stay) return null;
  if (!stay.location) {
    throw new Error(`stay ${stay.id} is admitted with no unit: the record is corrupt`);
  }
  return stay;
}

export interface OpenStayRequest {
  location?: string;
  class?: string;
  responsible_service?: string;
}

/** Open a new stay for a patient who has none admitted. */
export async function openStay(
  patientId: string,
  body: OpenStayRequest,
  idempotencyKey?: string,
): Promise<StayResponse> {
  try {
    logger.debug('Opening stay', { patientId, location: body.location });
    const stay = await api.post<StayResponse>(
      `/patients/${encodeURIComponent(patientId)}/stays`,
      body,
      idem(idempotencyKey),
    );
    logger.info('Stay opened', { patientId, stayId: stay.id });
    return stay;
  } catch (error) {
    logger.error('Failed to open stay', { patientId, error });
    throw error;
  }
}

/** Move an admitted stay to another unit. */
export async function transferStay(
  patientId: string,
  stayId: string,
  location: string,
  idempotencyKey?: string,
): Promise<StayResponse> {
  try {
    logger.debug('Transferring stay', { patientId, stayId, location });
    const stay = await api.post<StayResponse>(
      `/patients/${encodeURIComponent(patientId)}/stays/${encodeURIComponent(stayId)}:transfer`,
      { location },
      idem(idempotencyKey),
    );
    logger.info('Stay transferred', { patientId, stayId, location });
    return stay;
  } catch (error) {
    logger.error('Failed to transfer stay', { patientId, stayId, error });
    throw error;
  }
}

export interface DischargeStayRequest {
  /** Config-driven vocabulary (GET /config `stay.dispositions`). Required. */
  disposition: string;
  /** Required for exactly the dispositions config says need one. */
  destination?: string;
}

/** Close an admitted stay. */
export async function dischargeStay(
  patientId: string,
  stayId: string,
  body: DischargeStayRequest,
  idempotencyKey?: string,
): Promise<StayResponse> {
  try {
    logger.debug('Discharging stay', { patientId, stayId, disposition: body.disposition });
    const stay = await api.post<StayResponse>(
      `/patients/${encodeURIComponent(patientId)}/stays/${encodeURIComponent(stayId)}:discharge`,
      body,
      idem(idempotencyKey),
    );
    logger.info('Stay discharged', { patientId, stayId });
    return stay;
  } catch (error) {
    logger.error('Failed to discharge stay', { patientId, stayId, error });
    throw error;
  }
}

/**
 * Correct an erroneous stay event (HL7 ADT A11/A12/A13). Every correction is an
 * append: the placement log keeps what happened, not what is now believed.
 */
export async function cancelStayEvent(
  patientId: string,
  stayId: string,
  eventType: string,
  idempotencyKey?: string,
): Promise<StayResponse> {
  return api.post<StayResponse>(
    `/patients/${encodeURIComponent(patientId)}/stays/${encodeURIComponent(stayId)}:cancel`,
    { event_type: eventType },
    idem(idempotencyKey),
  );
}
